// shell/components/entity-fields/form-renderers/richtext-input.ts

import { createElement } from '../../../utils/dom';
import type { RichTextFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a simple rich text editor.
 * Uses contenteditable for basic formatting.
 */
export function renderRichTextInput<T>(
    field: RichTextFormField<T>,
    initialValue: unknown,
    onChange: (value: string) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--richtext',
    });

    // Toolbar
    const toolbar = createElement('div', {
        className: 'ev-richtext-toolbar',
    });

    const buttons = [
        { cmd: 'bold', icon: 'B', title: 'Bold' },
        { cmd: 'italic', icon: 'I', title: 'Italic' },
        { cmd: 'underline', icon: 'U', title: 'Underline' },
        { cmd: 'insertUnorderedList', icon: '•', title: 'Bullet list' },
        { cmd: 'insertOrderedList', icon: '1.', title: 'Numbered list' },
    ];

    for (const btn of buttons) {
        const button = createElement('button', {
            className: 'ev-richtext-btn',
            textContent: btn.icon,
            attributes: {
                type: 'button',
                title: btn.title,
                'aria-label': btn.title,
            },
            onClick: (e) => {
                e.preventDefault();
                document.execCommand(btn.cmd, false);
                editorEl.focus();
            },
        });
        if (btn.cmd === 'bold') button.style.fontWeight = 'bold';
        if (btn.cmd === 'italic') button.style.fontStyle = 'italic';
        if (btn.cmd === 'underline') button.style.textDecoration = 'underline';
        toolbar.appendChild(button);
    }

    // Editor area
    const editorEl = createElement('div', {
        className: 'ev-richtext-editor',
        attributes: {
            contenteditable: field.disabled ? 'false' : 'true',
            role: 'textbox',
            'aria-multiline': 'true',
            ...(field.placeholder ? { 'data-placeholder': field.placeholder } : {}),
        },
    });

    // Set initial value
    if (initialValue != null && initialValue !== '') {
        editorEl.innerHTML = String(initialValue);
    }

    // Handle input
    const handleInput = () => {
        onChange(editorEl.innerHTML);
    };

    // Handle paste - strip formatting if needed
    const handlePaste = (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') ?? '';
        document.execCommand('insertText', false, text);
    };

    editorEl.addEventListener('input', handleInput);
    editorEl.addEventListener('paste', handlePaste);

    container.appendChild(toolbar);
    container.appendChild(editorEl);

    return {
        element: container,
        getValue: () => editorEl.innerHTML,
        setValue: (value: unknown) => {
            editorEl.innerHTML = value != null ? String(value) : '';
        },
        validate: async () => true,
        focus: () => editorEl.focus(),
        dispose: () => {
            editorEl.removeEventListener('input', handleInput);
            editorEl.removeEventListener('paste', handlePaste);
        },
    };
}
